import React, { FC, MouseEventHandler, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useI18n } from '@affiliate-cabinet/i18n'
import { Copy0313, Link0113 } from '@affstore-ds/icons'
import { Button, ButtonSizeEnum, ButtonVariantEnum, CopyButton, Link, Modal, Stack, TextInput, Tooltip } from '@affstore-ds/uikit'

import { useAdvertiser } from '../../../../../../../entities/advertiser'
import { AffTrackInput } from '../../../../../../../features/AffTrackInput'
import { OffersModelsByAdvertiserSelect } from '../../../../../../../features/OffersModelsByAdvertiserSelect'
import { PromoBannerLandingsPageLinksSelect } from '../../../../../../../features/PromoBannerLandingsPageLinksSelect'
import { handleNavigate } from '../../../../../../../shared/navigation/handleNavigate'
import { buttonCopyTranslationKeys } from '../../../../../../../shared/translations/buttonCopy.translationKeys'
import { promoLandingsTranslationKeys } from '../../../../../landings/translations/PromoLandings.translationKeys'
import { bannerModalGenerateLinkTranslationKeys } from './translations/bannerModalGenerateLink.translationKeys'

import { usePromoBannerLinkGenerate } from '../../hooks/usePromoBannerLinkGenerate'

export const BannerModalGenerateLink: FC<{ advertiserId: number; bannerId: number; bannerLink: string; locale: string; size: string }> = ({
  advertiserId,
  bannerId,
  bannerLink,
  locale,
  size,
}) => {
  const { i18n } = useI18n()
  const navigate = useNavigate()

  const { advertiser } = useAdvertiser(advertiserId)
  const { form, link, modelError, tabOfferLink, tabOfferLinkText } = usePromoBannerLinkGenerate({
    advertiserAlias: advertiser?.alias || '',
    advertiserId,
    bannerLink,
    size,
  })

  const onClick: MouseEventHandler = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const isDisabledCopy = !form.values.landingLink || !form.values.model || Boolean(modelError)

  return (
    <Modal
      data-testid="banner-generate-link-modal"
      title={i18n.t(promoLandingsTranslationKeys.promoLandingsGenerateLink)}
      titleSize="l"
      buttonOpenProps={{
        'data-testid': 'card-button-generate-link',
        children: i18n.t(promoLandingsTranslationKeys.promoLandingsGenerateLink),
        elementSize: ButtonSizeEnum.M,
        elementVariant: ButtonVariantEnum.SecondaryMain,
        onClick,
        style: { flex: 1 },
      }}
      footerContent={
        <>
          {!modelError && (
            <CopyButton value={link}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? i18n.t(buttonCopyTranslationKeys.tooltipCopied) : ''} opened={copied} withArrow={false}>
                  <Button disabled={isDisabledCopy} onClick={copy} rightSection={<Copy0313 />} type="button">
                    {i18n.t(bannerModalGenerateLinkTranslationKeys.promoModalButtonCopyLink)}
                  </Button>
                </Tooltip>
              )}
            </CopyButton>
          )}
          {modelError && tabOfferLink && tabOfferLinkText && (
            <Link elementVariant={ButtonVariantEnum.PrimaryMain} href={tabOfferLink} onClick={handleNavigate(navigate, tabOfferLink)}>
              {tabOfferLinkText}
            </Link>
          )}
        </>
      }
      elementVariant="success"
      Icon={Link0113}
      size={420}
      centered
    >
      <form name="banner-generate-link-form">
        <Stack gap={16}>
          <PromoBannerLandingsPageLinksSelect
            name="landingLink"
            advertiserId={advertiserId}
            bannerId={bannerId}
            locale={locale}
            {...form.getInputProps('landingLink')}
          />
          <OffersModelsByAdvertiserSelect
            name="model"
            advertiserId={advertiserId}
            {...form.getInputProps('model')}
            error={form.getInputProps('model').error || modelError}
          />
          <AffTrackInput
            name="afftrack"
            placeholder={i18n.t(bannerModalGenerateLinkTranslationKeys.afftrackTypeHere)}
            {...form.getInputProps('afftrack')}
          />
          <TextInput
            data-testid="banner-link-value-to-copy"
            label={i18n.t(promoLandingsTranslationKeys.promoLandingsGenerateLink)}
            name="link-value"
            aria-readonly="true"
            disabled={isDisabledCopy}
            value={link}
            readOnly
          />
        </Stack>
      </form>
    </Modal>
  )
}
